import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { usePermissions } from '../hooks/usePermissions'
import Tabs, { TabList, TabItem, TabPanels, TabPanel } from '../components/ui/Tabs'
import Listbox from '../components/ui/Listbox'
import { FileText, BarChart3, TrendingUp, Users, Calendar, DollarSign, Building2 } from 'lucide-react'
import ReporteUsuarios from '../components/reportes/ReporteUsuarios'
import ReporteCitas from '../components/reportes/ReporteCitas'
import ReporteFichas from '../components/reportes/ReporteFichas'
import EstadisticasGenerales from '../components/reportes/EstadisticasGenerales'
import Tendencias from '../components/reportes/Tendencias'
import { clinicaService } from '../services/clinicaService'

export default function Reportes() {
  const { user, loading: authLoading } = useAuth()
  
  // Estado para selector de clínica y lista de clínicas
  const [clinicas, setClinicas] = useState([])
  const [selectedClinicaId, setSelectedClinicaId] = useState(null)
  const [activeTab, setActiveTab] = useState(0)
  
  const {
    isDueno,
    isVeterinario,
    isSuperAdmin,
    isAdmin,
    isVeterinarioAdmin,
  } = usePermissions()
  const veterinarioData = user?.veterinarios?.[0]
  // ID de clínica para peticiones: vet admin = su clínica; ADMIN/SUPER = selector o todas (null)
  const userClinicaId = authLoading || !user
    ? undefined
    : (isVeterinarioAdmin
      ? (veterinarioData?.id_clinica ?? veterinarioData?.idClinica ?? undefined)
      : (selectedClinicaId ? Number(selectedClinicaId) : null))

  // Cargar clínicas para el selector (SUPERADMIN/ADMIN) o para mostrar nombre (veterinario admin)
  useEffect(() => {
    if (isSuperAdmin || isAdmin || isVeterinarioAdmin) {
      fetchClinicas()
    }
  }, [isSuperAdmin, isAdmin, isVeterinarioAdmin, veterinarioData?.id_clinica, veterinarioData?.idClinica])


  const fetchClinicas = async () => {
    try {
      const params = { per_page: 100, activa: true }
      // Si es veterinario admin, solo obtener su clínica
      if (isVeterinarioAdmin) {
        const vetClinicaId = veterinarioData?.id_clinica || veterinarioData?.idClinica
        if (vetClinicaId) {
          params.id_clinica = Number(vetClinicaId)
        }
      }
      const response = await clinicaService.getAll(params)
      if (response.success) {
        const clinicasData = response.data?.data || response.data || []
        setClinicas(clinicasData)
      }
    } catch (err) {
      console.error('Error fetching clínicas:', err)
    }
  }

  // Tabs para DUEÑO (solo sus reportes)
  const duenoTabs = [
    { id: 'estadisticas', name: 'Mis Estadísticas', icon: BarChart3 },
    { id: 'citas', name: 'Mis Citas', icon: Calendar },
    { id: 'fichas', name: 'Fichas de Mis Mascotas', icon: FileText },
    { id: 'gastos', name: 'Mis Gastos', icon: DollarSign },
  ]

  // Tabs completos para otros roles
  const fullTabs = [
    { id: 'estadisticas', name: 'Estadísticas Generales', icon: BarChart3 },
    { id: 'usuarios', name: 'Usuarios', icon: Users },
    { id: 'citas', name: 'Citas', icon: Calendar },
    { id: 'fichas', name: 'Fichas Clínicas', icon: FileText },
    { id: 'tendencias', name: 'Tendencias', icon: TrendingUp },
  ]

  const tabs = isDueno ? duenoTabs : fullTabs

  // Mostrar loading mientras se carga el usuario (después de todos los hooks)
  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Cargando reportes...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Reportes</h1>
          <p className="mt-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
            {isDueno 
              ? 'Reportes y estadísticas de tus mascotas y gastos'
              : isVeterinarioAdmin
              ? 'Reportes y estadísticas de tu clínica'
              : isSuperAdmin || isAdmin
              ? selectedClinicaId 
                ? `Reportes y estadísticas de la clínica seleccionada`
                : 'Reportes y estadísticas del sistema completo'
              : 'Reportes y estadísticas del sistema'}
          </p>
        </div>
        
        {/* Selector de clínica para SUPERADMIN/ADMIN */}
        {(isSuperAdmin || isAdmin) && (
          <div className="w-full sm:w-64">
            <Listbox
              label="Filtrar por Clínica"
              options={[
                { value: '', label: 'Todas las clínicas' },
                ...clinicas.map((clinica) => ({
                  value: String(clinica.id_clinica || clinica.idClinica),
                  label: clinica.nombre
                }))
              ]}
              value={selectedClinicaId ? String(selectedClinicaId) : ''}
              onChange={(value) => setSelectedClinicaId(value ? Number(value) : null)}
            />
          </div>
        )}
        
        {/* Información de clínica para veterinario admin */}
        {isVeterinarioAdmin && userClinicaId && (
          <div className="w-full sm:w-auto">
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
              <p className="text-sm text-blue-700 dark:text-blue-300 flex items-center">
                <Building2 className="w-4 h-4 mr-2" />
                Clínica: {clinicas.find(c => (c.id_clinica || c.idClinica) === userClinicaId)?.nombre || 'Tu clínica'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <Tabs selectedIndex={activeTab} onChange={setActiveTab}>
        <TabList className="border-b border-gray-200 dark:border-gray-700">
          {tabs.map((tab, index) => {
            const Icon = tab.icon
            return (
              <TabItem key={tab.id} className="flex items-center space-x-2">
                <Icon className="w-5 h-5" />
                <span>{tab.name}</span>
              </TabItem>
            )
          })}
        </TabList>

        <TabPanels className="mt-6">
          <TabPanel>
            {/* Para veterinarios admin, usamos id_clinica del /me endpoint directamente */}
            <EstadisticasGenerales idClinica={userClinicaId} />
          </TabPanel>
          {!isDueno && (
            <TabPanel>
              <ReporteUsuarios idClinica={userClinicaId} />
            </TabPanel>
          )}
          <TabPanel>
            <ReporteCitas idClinica={userClinicaId} />
          </TabPanel>
          <TabPanel>
            <ReporteFichas idClinica={userClinicaId} />
          </TabPanel>
          {isDueno && (
            <TabPanel>
              <div className="text-center py-12">
                <DollarSign className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-500 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Reporte de Gastos
                </h3>
                <p className="text-gray-500 dark:text-gray-400">
                  Próximamente: Visualización de tus gastos veterinarios
                </p>
              </div>
            </TabPanel>
          )}
          {!isDueno && (
            <TabPanel>
              <Tendencias idClinica={userClinicaId} />
            </TabPanel>
          )}
        </TabPanels>
      </Tabs>
    </div>
  )
}
